import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

/**
 * BuildProgressBadge — 對話標題列右側的「AI 更新中」狀態列
 *
 * 顯示：轉圈 + 目前階段 + 已耗時 / 預估總時間 + 進度條 + 「時間依資料量而定」提示。
 *
 * 預估(ETA)怎麼來 — 三層,由準到粗:
 *   1. 這台瀏覽器過去實際跑完的耗時(localStorage,輕/重各留 20 筆取中位數)。同一個帳號的
 *      操作習慣相近,跑過幾次後預估會自己收斂到符合實際。
 *   2. 沒有歷史 → 用內建預設值(輕 75s / 重 240s)。
 *   3. 本輪偵測到重工具(建 KB、爬站、生 flow、生語音…)→ 直接套「重」那一桶。
 *
 * ⚠️ 沒有用 thinking token 去外推剩餘時間 — 模型 thinking 的長短跟總耗時沒有穩定相關
 *    (真正花時間的是 tool: 爬站、算 embedding、生 MP3,那段完全不 thinking)。
 *    thinking / toolCalls 只拿來判斷「現在在哪個階段」,讓文字有內容,不拿來算秒數。
 *
 * 超過預估不會卡在 99% 騙人 — 文案改成「資料量較大,仍在處理」,進度條轉不定長動畫。
 */

const STORE_KEY = 'qubby.builderTurnMs.v1';
const KEEP = 20;                 // 每桶保留最近幾筆
const DEFAULT_ETA = { light: 75_000, heavy: 240_000 };
const MIN_RECORD_MS = 3_000;     // 太短的(送錯字、被擋下)不進歷史,會把中位數拉歪

// 這些 skill 一跑就是分鐘級：爬整站、算 embedding、生語音、跑 E2E 通話
const HEAVY_SKILLS = [
    'build-ivr-avatar', 'build-kb-from-files', 'crawl-site-to-kb', 'pdf-to-rag-kb',
    'generate-ivr-flow', 'build-survey-flow', 'migrate-ivr-avatar', 'generate-celebrity-persona',
    'generate-ivr-greeting', 'import-outbound-list', 'import-member-list', 'import-directory',
    'test-ivr-e2e', 'test-ivr-kb-e2e', 'test-survey-e2e', 'test-webcall-voice-e2e',
    'add-api-nodes', 'add-mcp-nodes', 'refine-agent-prompt',
];
const HEAVY_TOOL_COUNT = 10;     // 沒中 skill 名單但工具呼叫已經很多 → 也當重的

function readStore() {
    try {
        const raw = JSON.parse(localStorage.getItem(STORE_KEY) || '{}');
        return {
            light: Array.isArray(raw.light) ? raw.light.filter(Number.isFinite) : [],
            heavy: Array.isArray(raw.heavy) ? raw.heavy.filter(Number.isFinite) : [],
        };
    } catch {
        return { light: [], heavy: [] };
    }
}

function recordDuration(bucket, ms) {
    if (!Number.isFinite(ms) || ms < MIN_RECORD_MS) return;
    try {
        const store = readStore();
        store[bucket] = [...store[bucket], Math.round(ms)].slice(-KEEP);
        localStorage.setItem(STORE_KEY, JSON.stringify(store));
    } catch { /* 無痕模式 / quota 滿 → 就用預設值,不影響功能 */ }
}

function median(arr) {
    if (!arr.length) return null;
    const s = [...arr].sort((a, b) => a - b);
    const mid = s.length >> 1;
    return s.length % 2 ? s[mid] : Math.round((s[mid - 1] + s[mid]) / 2);
}

/** 本輪是不是「重」的 — 看有沒有呼叫到重 skill,或工具呼叫已經很多 */
function isHeavyTurn(turn) {
    const calls = turn?.toolCalls || [];
    if (calls.length >= HEAVY_TOOL_COUNT) return true;
    return calls.some((c) => {
        const blob = `${c.name || ''} ${c.input || ''}`.toLowerCase();
        return HEAVY_SKILLS.some((s) => blob.includes(s));
    });
}

function mmss(ms) {
    const total = Math.max(0, Math.round(ms / 1000));
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** 從 tool input 的 JSON 片段撈出可讀的名字(streaming 中的 JSON 可能還沒收尾) */
function toolLabel(call) {
    if (!call) return null;
    const m = /"(?:skill|description|file_path|pattern|query|url)"\s*:\s*"([^"]{1,40})/.exec(call.input || '');
    return m ? m[1] : call.name || null;
}

export function BuildProgressBadge({ chat }) {
    const { t } = useTranslation('workspace');
    const { isStreaming, turnStartedAt, currentTurn } = chat;

    const [now, setNow] = useState(() => Date.now());
    const [store, setStore] = useState(readStore);
    const heavyRef = useRef(false);      // 本輪的重/輕判定(單向:一旦判重就不再降回輕)
    const prevStreamingRef = useRef(false);

    // 只有跑的時候才開計時器
    useEffect(() => {
        if (!isStreaming) return;
        setNow(Date.now());
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [isStreaming]);

    // 本輪只要一度被判為重就固定住 — 工具是逐個進來的,不能讓預估在輕/重之間跳動
    if (isStreaming && !heavyRef.current && isHeavyTurn(currentTurn)) heavyRef.current = true;

    // turn 結束 → 把這次實際耗時寫進歷史,下次預估更準
    useEffect(() => {
        if (prevStreamingRef.current && !isStreaming) {
            if (turnStartedAt) recordDuration(heavyRef.current ? 'heavy' : 'light', Date.now() - turnStartedAt);
            setStore(readStore());
            heavyRef.current = false;
        }
        prevStreamingRef.current = isStreaming;
    }, [isStreaming, turnStartedAt]);

    if (!isStreaming || !turnStartedAt) return null;

    const heavy = heavyRef.current;
    const bucket = heavy ? 'heavy' : 'light';
    const eta = median(store[bucket]) ?? DEFAULT_ETA[bucket];
    const elapsed = Math.max(0, now - turnStartedAt);
    const overtime = elapsed > eta;
    const pct = overtime ? 100 : Math.min(96, (elapsed / eta) * 100);

    // 階段 — 用 thinking / toolCalls 的現況判斷,純粹讓文字有內容
    const calls = currentTurn?.toolCalls || [];
    const running = calls.length && calls[calls.length - 1].result === null ? calls[calls.length - 1] : null;
    let phase;
    // 後端 pushStatus() 的白話狀態（壓縮記憶等長時間動作）優先 —— 那段期間沒有任何
    // thinking / toolCall 事件，不覆蓋的話會一路顯示「準備中」讓用戶以為卡住。
    if (currentTurn?.statusNote) phase = currentTurn.statusNote;
    else if (running) phase = t('progress.phase.running', { tool: toolLabel(running) });
    else if (calls.length) phase = t('progress.phase.wrapUp');
    else if (currentTurn?.thinking) phase = t('progress.phase.thinking');
    else phase = t('progress.phase.starting');

    return (
        <div className="w-[300px] shrink-0" role="status" aria-live="polite">
            <div className="flex items-baseline gap-1.5">
                <Loader2 size={12} className="animate-spin text-accent-700 shrink-0 self-center" strokeWidth={2} />
                <span className="text-[11px] font-medium text-ink-700 truncate">
                    {overtime ? t('progress.stillWorking') : t('progress.updating')}
                </span>
                <span className="ml-auto font-mono text-[10px] text-ink-400 tabular-nums shrink-0">
                    {overtime ? mmss(elapsed) : t('progress.timeOfEta', { elapsed: mmss(elapsed), eta: mmss(eta) })}
                </span>
            </div>
            <div className="mt-1 h-1 rounded-full bg-cream-200 overflow-hidden">
                <div
                    className={[
                        'h-full rounded-full bg-accent-500',
                        overtime ? 'animate-pulse' : 'transition-[width] duration-1000 ease-linear',
                    ].join(' ')}
                    style={{ width: `${pct}%` }}
                />
            </div>
            <div className="mt-0.5 text-[10px] leading-tight text-ink-400 truncate">
                {currentTurn?.statusNote ? phase : (overtime ? t('progress.hintOvertime') : `${phase} · ${t('progress.hint')}`)}
            </div>
        </div>
    );
}
